import { useEffect, useRef, useState } from "react";

import type { SubscriptionSummaryProps } from "@/features/auth/subscription/components/SubscriptionType.ts";

import FullLogo from "@/assets/full-logo.svg?react";
import { Button } from "@/components/button";
import { Input } from "@/components/field";
import { env } from "@/config/env";
import { toIsoCurrency } from "@/features/auth/subscription/utils/purchaseEvent";
import { pushDataLayer } from "@/lib/dataLayer";

export function SubscriptionSummary({
  initialCoupon,
  isSubmitting = false,
  onPromoApplied,
  onSubmit,
  onTermsChange,
  plan,
  showPromo = false,
  termsAccepted = false,
}: SubscriptionSummaryProps) {
  const [promoCode, setPromoCode] = useState("");
  const [isApplying, setIsApplying] = useState(false);
  const [applyError, setApplyError] = useState<null | string>(null);
  const [applied, setApplied] = useState<{
    code: string;
    discount_amount: number;
    final_amount: number;
    original_amount: number;
  } | null>(null);

  const formatPrice = (price: number | string) =>
    Number(price).toLocaleString("en-US");

  const amountCents = Math.round(Number(plan.price) * 100);
  const currencyCode = toIsoCurrency(plan.currency, "USD").toLowerCase();

  const subtotal = applied
    ? (applied.original_amount / 100).toFixed(2)
    : plan.price;
  const total = applied ? (applied.final_amount / 100).toFixed(2) : plan.price;

  const applyCode = async (code: string): Promise<boolean> => {
    const couponCode = code.trim();

    if (!couponCode || applied) return !!applied;
    setIsApplying(true);
    setApplyError(null);
    try {
      const base = env.VITE_PHP_URL.replace(/\/$/, "");

      //TODO transfer this service to Node
      const res = await fetch(`${base}/api/stripe/promotion-code/apply`, {
        body: JSON.stringify({
          amount: amountCents,
          code: couponCode,
          currency: currencyCode,
        }),
        headers: { "Content-Type": "application/json" },
        method: "POST",
      });
      const data = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(data?.message || "Invalid code");
      }
      console.log("[NLC] Promo apply success", {
        data,
        sent: {
          amount: amountCents,
          code: couponCode,
          currency: currencyCode,
        },
      });
      setApplied({
        code: data.code || couponCode,
        discount_amount: data.discount_amount,
        final_amount: data.final_amount,
        original_amount: data.original_amount,
      });
      setPromoCode(data.code || couponCode);
      // Notify parent so the subscribed flow can attach the promotion code in Stripe
      onPromoApplied?.(
        data.code || couponCode,
        data.promotion_code_id ?? null,
        data.final_amount,
      );

      return true;
    } catch {
      setApplyError("Invalid code.");

      return false;
    } finally {
      setIsApplying(false);
    }
  };

  const handleApply = (): Promise<boolean> => applyCode(promoCode);

  const hasAutoAppliedRef = useRef(false);

  useEffect(() => {
    if (!showPromo || !initialCoupon || hasAutoAppliedRef.current) return;
    hasAutoAppliedRef.current = true;
    setPromoCode(initialCoupon);
    void applyCode(initialCoupon);
  }, [showPromo, initialCoupon]);

  const handleSubscribePress = async () => {
    pushDataLayer({
      event: "subscribe",
      page_url: "subscription",
      section: "checkout_submit",
    });

    if (showPromo && promoCode.trim() && !applied) {
      const ok = await handleApply();

      if (!ok) return;
      // Give React a tick to propagate onPromoApplied state up to the SubscriptionPage
      // and down into SubscriptionForm props before submitting.
      await new Promise((resolve) => setTimeout(resolve, 0));
    }
    onSubmit?.();
  };

  return (
    <div className="space-y-6">
      {/* White background section - up to Total due today */}
      <div className="bg-bg-primary border border-border-secondary rounded-2xl shadow-sm p-[32px]">
        {/* Header with Logo */}
        <div className="flex items-center gap-2 mb-6">
          <FullLogo className="w-[162px] h-[32px]" />
        </div>

        {/* Plan Details */}
        <div className="mb-6">
          <div className="text-sm text-text-tertiary mb-1">
            Subscribe to {plan.name}
          </div>
          <div className="flex items-center gap-1">
            <span className="text-4xl font-bold text-text-primary">
              {plan.currency === "US$" ? "$" : plan.currency}
              {formatPrice(plan.price)}
            </span>
            <span className="text-[12px] text-text-tertiary">
              per <br /> {plan.billingCycle}
            </span>
          </div>
        </div>

        {/* Plan Name */}
        <div className="mb-4">
          <div className="text-xs text-text-tertiary mt-1 flex justify-between">
            <div className="text-sm font-semibold text-text-primary">
              {plan.name}
            </div>
            <span className="font-semibold text-text-primary">
              {plan.currency}
              {formatPrice(plan.price)}
            </span>
          </div>
        </div>

        {/* Plan Description */}
        <div className="mb-8">
          <p className="text-[12px] text-text-tertiary">{plan.description}</p>
        </div>

        {/* Pricing Breakdown */}
        <div className="space-y-3 mb-6">
          <div className="flex justify-between items-center">
            <span className="text-sm text-text-tertiary">Subtotal</span>
            <span className="text-sm font-medium text-text-primary">
              {plan.currency}
              {formatPrice(subtotal)}
            </span>
          </div>

          {applied && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-text-tertiary">Discount</span>
              <span className="text-sm font-medium text-text-primary">
                - {plan.currency}
                {formatPrice((applied.discount_amount / 100).toFixed(2))}
              </span>
            </div>
          )}

          {showPromo && (
            <>
              <div className="flex w-full items-stretch rounded-md border border-border-primary overflow-hidden">
                <Input
                  className={
                    "flex-1 rounded-none border-0 focus-visible:ring-0 " +
                    (applyError
                      ? " text-text-error-primary placeholder:text-text-error-primary"
                      : "")
                  }
                  onChange={(e) =>
                    setPromoCode((e.target as HTMLInputElement).value)
                  }
                  placeholder="Add promotion code"
                  readOnly={!!applied}
                  type="text"
                  value={promoCode}
                />
                <Button
                  className="rounded-none border-0 border-l border-border-primary"
                  color="gray"
                  isDisabled={!!applied || !promoCode.trim()}
                  isLoading={isApplying}
                  loadingLabel="Applying"
                  onPress={handleApply}
                  size="sm"
                  variant="secondary"
                >
                  {applied ? "Applied" : "Apply"}
                </Button>
              </div>
              {applied && (
                <p className="text-sm text-text-success-primary mt-2">
                  Coupon applied.
                </p>
              )}
              {!applied && applyError && (
                <p className="text-sm text-text-error-primary mt-2">
                  Invalid code.
                </p>
              )}
            </>
          )}
        </div>

        {/* Total */}
        <div className="pt-4">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-text-primary">
              Total due today
            </span>
            <span className="text-lg font-bold text-text-primary">
              {plan.currency}
              {formatPrice(total)}
            </span>
          </div>
        </div>
      </div>

      {/* Background color section - Save details, Terms, and Submit button */}
      <div className="space-y-6">
        {/* Terms and conditions */}
        <div className="text-xs text-text-tertiary space-y-2">
          <label className="flex items-start gap-3 cursor-pointer items-center">
            <input
              checked={termsAccepted}
              className="mt-1"
              onChange={(e) => onTermsChange?.(e.target.checked)}
              required
              type="checkbox"
            />
            <p>
              By checking the box, l agree to the{" "}
              <a
                className="text-fg-brand-tertiary underline"
                href="https://nlc.com/terms-of-service/"
                rel="noreferrer"
                target="_blank"
              >
                Terms of Service
              </a>{" "}
              and{" "}
              <a
                className="text-fg-brand-tertiary underline"
                href="https://nlc.com/privacy-policy/"
                rel="noreferrer"
                target="_blank"
              >
                Privacy Policy
              </a>{" "}
              of NoLimit Creatives.
            </p>
          </label>
          <p className="my-5">
            By confirming your subscription, you allow No Limit Creatives to
            charge future payments according to the stipulated terms. You can
            always cancel your subscription.
          </p>
        </div>

        {/* Submit Button */}
        <Button
          className="w-full"
          isDisabled={!termsAccepted || isSubmitting || isApplying}
          isLoading={isSubmitting}
          loadingLabel="Processing payment"
          onPress={handleSubscribePress}
          size="lg"
          variant="primary"
        >
          Subscribe
        </Button>
      </div>
    </div>
  );
}
